import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  Users, 
  Target, 
  Calendar, 
  Globe2,
  Plus, 
  CheckCircle,
  Clock,
  AlertCircle,
  UserPlus,
  BarChart3,
  Pencil,
  Trash2,
  Briefcase,
  Paperclip,
  Megaphone,
  Loader2
} from 'lucide-react';
import { toDateInputValue, sortWeeksChronologically, findWeekContainingDate } from '../utils/weekDates';
import {
  emptyKeywordRow,
  formatKeywordPairsForDisplay,
  pairsToFormRows,
  serializeKeywordVolumePairs
} from '../utils/targetKeywords';

const getCurrentMonthLabel = () => {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
};

const emptyTargetForm = (weekId = '') => ({
  user_id: '',
  week_id: weekId,
  client_id: '',
  title: '',
  client_url: '',
  description: '',
  keywordRows: [emptyKeywordRow()],
  status: 'pending',
  evidence: ''
});

const formatSearchVolume = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return n.toLocaleString('en-GB');
};

const getDefaultLbMonthId = (months) => {
  if (!Array.isArray(months) || months.length === 0) return '';
  const currentLabel = getCurrentMonthLabel();
  const current = months.find((m) => m.month_label === currentLabel);
  const fallback = current || months[months.length - 1] || months[0];
  return fallback ? String(fallback.id) : '';
};

const getCurrentWeekId = (weekRows) => {
  if (!Array.isArray(weekRows) || weekRows.length === 0) return null;
  const now = new Date();
  const nowUtcMs = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());

  const current = weekRows.find((week) => {
    const start = new Date(week.start_date);
    const end = new Date(week.end_date);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;
    const startUtc = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate());
    const endUtc = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate());
    return nowUtcMs >= startUtc && nowUtcMs <= endUtc;
  });
  return current?.id || null;
};

const extractRootDomain = (urlValue) => {
  const raw = String(urlValue || '').trim().toLowerCase();
  if (!raw) return '';
  let parsed;
  try {
    parsed = new URL(/^[a-z][a-z0-9+\-.]*:\/\//i.test(raw) ? raw : `https://${raw.replace(/^\/+/, '')}`);
  } catch (error) {
    return '';
  }
  const host = String(parsed.hostname || '').replace(/^www\./i, '');
  if (!host) return '';
  const parts = host.split('.').filter(Boolean);
  if (parts.length <= 2) return host;
  const lastTwo = `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
  const compoundSuffixes = new Set([
    'co.uk', 'org.uk', 'gov.uk', 'ac.uk',
    'com.au', 'net.au', 'org.au',
    'co.nz', 'org.nz',
    'co.za', 'org.za',
    'com.br', 'net.br',
    'co.in', 'org.in'
  ]);
  if (compoundSuffixes.has(lastTwo) && parts.length >= 3) {
    return `${parts[parts.length - 3]}.${lastTwo}`;
  }
  return lastTwo;
};

const formatDuration = (seconds) => {
  const total = Math.max(0, Number(seconds) || 0);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const AdminDashboard = () => {
  const { register, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [clients, setClients] = useState([]);
  const [lbMonths, setLbMonths] = useState([]);
  const [dprMonths, setDprMonths] = useState([]);
  const [allLbClientMonthlyTargets, setAllLbClientMonthlyTargets] = useState([]);
  const [allDprClientMonthlyTargets, setAllDprClientMonthlyTargets] = useState([]);
  const [targets, setTargets] = useState([]);
  const [allTargets, setAllTargets] = useState([]);
  const [lbContentRows, setLbContentRows] = useState([]);
  const [dprContentRows, setDprContentRows] = useState([]);
  const [monthlyTargetDrafts, setMonthlyTargetDrafts] = useState({});
  const [dprMonthlyTargetDrafts, setDprMonthlyTargetDrafts] = useState({});
  const [savingMonthlyTargetByMonthId, setSavingMonthlyTargetByMonthId] = useState({});
  const [savingDprMonthlyTargetByMonthId, setSavingDprMonthlyTargetByMonthId] = useState({});
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Form states
  const [showUserForm, setShowUserForm] = useState(false);
  const [showWeekForm, setShowWeekForm] = useState(false);
  const [showTargetForm, setShowTargetForm] = useState(false);
  const [showClientForm, setShowClientForm] = useState(false);
  const [showImportForm, setShowImportForm] = useState(false);
  const [showLbImportForm, setShowLbImportForm] = useState(false);
  const [showDprImportForm, setShowDprImportForm] = useState(false);
  const [showInactiveClients, setShowInactiveClients] = useState(false);
  const [performanceScope, setPerformanceScope] = useState('all_time');
  const [performanceDateRange, setPerformanceDateRange] = useState('all');
  const [showLbImportResults, setShowLbImportResults] = useState(false);
  const [lbImportResults, setLbImportResults] = useState(null);
  const [showDprImportResults, setShowDprImportResults] = useState(false);
  const [dprImportResults, setDprImportResults] = useState(null);
  const [reportDateFrom, setReportDateFrom] = useState('');
  const [reportDateTo, setReportDateTo] = useState('');
  const [reportClientId, setReportClientId] = useState('');
  const [reportUserId, setReportUserId] = useState('');
  const [reportService, setReportService] = useState('all');
  const [expandedFrequencyDomain, setExpandedFrequencyDomain] = useState('');
  const [frequencyClientId, setFrequencyClientId] = useState('');
  const [frequencyDateFrom, setFrequencyDateFrom] = useState('');
  const [frequencyDateTo, setFrequencyDateTo] = useState('');
  const [frequencyReportActive, setFrequencyReportActive] = useState(false);
  const [frequencyAppliedFilters, setFrequencyAppliedFilters] = useState({
    clientId: '',
    dateFrom: '',
    dateTo: ''
  });
  const [importFile, setImportFile] = useState(null);
  const [lbImportFile, setLbImportFile] = useState(null);
  const [dprImportFile, setDprImportFile] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editingWeek, setEditingWeek] = useState(null);
  const [editingTarget, setEditingTarget] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [newUser, setNewUser] = useState({ username: '', full_name: '', email: '', password: '', role: 'user' });
  const [newWeek, setNewWeek] = useState({ week_number: '', year: '', start_date: '', end_date: '' });
  const [newTarget, setNewTarget] = useState(emptyTargetForm());
  const [newClient, setNewClient] = useState({
    name: '',
    website_url: '',
    status: 'active',
    lb_monthly_target: '',
    dpr_monthly_target: '',
    uses_content: true,
    uses_link_building: true,
    uses_digital_pr: false,
    content_monthly_target: ''
  });
  const [selectedLbTargetMonthId, setSelectedLbTargetMonthId] = useState('');
  const [selectedLbTargetsMonthId, setSelectedLbTargetsMonthId] = useState('');
  const [selectedDprTargetMonthId, setSelectedDprTargetMonthId] = useState('');
  const [selectedDprTargetsMonthId, setSelectedDprTargetsMonthId] = useState('');

  const headerWeekDateInputValue = useMemo(
    () => toDateInputValue(selectedWeek?.start_date),
    [selectedWeek]
  );

  const targetFormWeekDateInputValue = useMemo(() => {
    const weekRow = weeks.find((w) => String(w.id) === String(newTarget.week_id));
    return toDateInputValue(weekRow?.start_date);
  }, [weeks, newTarget.week_id]);

  const targetFormWeekSummary = useMemo(() => {
    const weekRow = weeks.find((w) => String(w.id) === String(newTarget.week_id));
    if (!weekRow) return '';
    return `Week ${weekRow.week_number}, ${weekRow.year}`;
  }, [weeks, newTarget.week_id]);

  const keywordFetchSignature = useMemo(
    () =>
      JSON.stringify(
        (newTarget.keywordRows || []).map((row) => [
          String(row.keyword || '').trim(),
          row.search_volume === '' || row.search_volume === null || row.search_volume === undefined
            ? ''
            : 'has'
        ])
      ),
    [newTarget.keywordRows]
  );

  const isValidHttpUrl = (value) => {
    try {
      const parsed = new URL(value);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch (error) {
      return false;
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, weeksRes, clientsRes, lbMonthsRes, dprMonthsRes, allTargetsRes, lbContentRes, dprContentRes, lbClientTargetsRes, dprClientTargetsRes] = await Promise.all([
        axios.get('/api/users'),
        axios.get('/api/weeks'),
        axios.get('/api/clients?include_inactive=true'),
        axios.get('/api/link-building/months'),
        axios.get('/api/digital-pr/months'),
        axios.get('/api/targets'),
        axios.get('/api/link-building/content'),
        axios.get('/api/digital-pr/content'),
        axios.get('/api/link-building/client-monthly-targets'),
        axios.get('/api/digital-pr/client-monthly-targets')
      ]);
      
      setUsers(usersRes.data);
      const sortedWeeks = sortWeeksChronologically(weeksRes.data);
      setWeeks(sortedWeeks);
      setClients(clientsRes.data);
      setLbMonths(lbMonthsRes.data);
      setDprMonths(dprMonthsRes.data);
      setAllTargets(allTargetsRes.data || []);
      setLbContentRows(lbContentRes.data || []);
      setDprContentRows(dprContentRes.data || []);
      setAllLbClientMonthlyTargets(lbClientTargetsRes.data || []);
      setAllDprClientMonthlyTargets(dprClientTargetsRes.data || []);
      if (sortedWeeks.length > 0) {
        const currentWeekId = getCurrentWeekId(sortedWeeks);
        const defaultWeek =
          sortedWeeks.find((w) => w.id === currentWeekId) || sortedWeeks[sortedWeeks.length - 1];
        setSelectedWeek((current) => {
          if (!current) return defaultWeek;
          const stillExists = sortedWeeks.find((w) => w.id === current.id);
          return stillExists || defaultWeek;
        });
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTargets = useCallback(async () => {
    if (!selectedWeek) return;
    
    try {
      const response = await axios.get(`/api/targets?week_id=${selectedWeek.id}`);
      setTargets(response.data);
    } catch (error) {
      toast.error('Failed to load targets');
    }
  }, [selectedWeek]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchTargets();
  }, [fetchTargets]);

  // Fetch volumes for keyword rows that are missing SV; signature avoids loops when volumes update.
  useEffect(() => {
    if (!showTargetForm) return undefined;
    const rows = Array.isArray(newTarget.keywordRows) ? newTarget.keywordRows : [];
    const fetchList = rows
      .filter(
        (row) =>
          String(row.keyword || '').trim() &&
          (row.search_volume === '' || row.search_volume === null || row.search_volume === undefined)
      )
      .map((row) => String(row.keyword || '').trim());

    if (fetchList.length === 0) return undefined;

    const timer = setTimeout(async () => {
      setNewTarget((prev) => ({
        ...prev,
        keywordRows: (prev.keywordRows || []).map((row) =>
          String(row.keyword || '').trim() &&
          (row.search_volume === '' || row.search_volume === null || row.search_volume === undefined)
            ? { ...row, loading: true }
            : row
        )
      }));

      try {
        const response = await axios.post('/api/targets/keyword-search-volume', {
          keywords: fetchList
        });
        const volumes = response.data?.volumes || {};
        setNewTarget((prev) => ({
          ...prev,
          keywordRows: (prev.keywordRows || []).map((row) => {
            const kw = String(row.keyword || '').trim();
            if (!kw) return { ...row, loading: false };
            const needsVolume =
              row.search_volume === '' || row.search_volume === null || row.search_volume === undefined;
            if (!needsVolume) return { ...row, loading: false };
            const vol = volumes[kw];
            return {
              ...row,
              loading: false,
              search_volume: vol === null || vol === undefined ? '' : String(vol)
            };
          })
        }));
      } catch (error) {
        setNewTarget((prev) => ({
          ...prev,
          keywordRows: (prev.keywordRows || []).map((row) => ({ ...row, loading: false }))
        }));
        toast.error(error.response?.data?.error || 'Failed to fetch search volume');
      }
    }, 700);

    return () => clearTimeout(timer);
    // Intentionally keyed off keywordFetchSignature (not keywordRows) to avoid volume-update loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTargetForm, keywordFetchSignature]);

  useEffect(() => {
    if (!selectedLbTargetMonthId && lbMonths.length > 0) {
      setSelectedLbTargetMonthId(getDefaultLbMonthId(lbMonths));
    }
  }, [lbMonths, selectedLbTargetMonthId]);

  useEffect(() => {
    if (!selectedLbTargetsMonthId && lbMonths.length > 0) {
      setSelectedLbTargetsMonthId(getDefaultLbMonthId(lbMonths));
    }
  }, [lbMonths, selectedLbTargetsMonthId]);

  useEffect(() => {
    if (!selectedDprTargetMonthId && dprMonths.length > 0) {
      setSelectedDprTargetMonthId(getDefaultLbMonthId(dprMonths));
    }
  }, [dprMonths, selectedDprTargetMonthId]);

  useEffect(() => {
    if (!selectedDprTargetsMonthId && dprMonths.length > 0) {
      setSelectedDprTargetsMonthId(getDefaultLbMonthId(dprMonths));
    }
  }, [dprMonths, selectedDprTargetsMonthId]);

  useEffect(() => {
    if (!isAdmin && activeTab === 'users') {
      setActiveTab('overview');
    }
  }, [isAdmin, activeTab]);

  const createUser = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await axios.put(`/api/users/${editingUser.id}`, newUser);
        toast.success('User updated successfully');
      } else {
        const ok = await register(newUser);
        if (!ok) {
          return;
        }
      }
      setShowUserForm(false);
      setEditingUser(null);
      setNewUser({ username: '', full_name: '', email: '', password: '', role: 'user' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create user');
    }
  };

  const createWeek = async (e) => {
    e.preventDefault();
    try {
      if (editingWeek) {
        await axios.put(`/api/weeks/${editingWeek.id}`, newWeek);
        toast.success('Week updated successfully');
      } else {
        await axios.post('/api/weeks', newWeek);
        toast.success('Week created successfully');
      }
      setShowWeekForm(false);
      setEditingWeek(null);
      setNewWeek({ week_number: '', year: '', start_date: '', end_date: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create week');
    }
  };

  const createTarget = async (e) => {
    e.preventDefault();
    if (!newTarget.week_id) {
      toast.error('Choose a week (pick any date that falls in that week)');
      return;
    }
    try {
      const serializedKeywords = serializeKeywordVolumePairs(newTarget.keywordRows || []);
      const payload = {
        ...newTarget,
        user_id: Number(newTarget.user_id),
        week_id: Number(newTarget.week_id),
        client_id:
          newTarget.client_id === '' || newTarget.client_id === undefined
            ? null
            : Number(newTarget.client_id),
        keyword: serializedKeywords.keyword,
        search_volume: serializedKeywords.search_volume
      };
      delete payload.keywordRows;

      if (editingTarget) {
        await axios.put(`/api/targets/${editingTarget.id}`, payload);
        toast.success('Target updated successfully');
      } else {
        await axios.post('/api/targets', payload);
        toast.success('Target created successfully');
      }
      setShowTargetForm(false);
      setEditingTarget(null);
      setNewTarget(emptyTargetForm());
      fetchTargets();
    } catch (error) {
      toast.error('Failed to create target');
    }
  };

  const createClient = async (e) => {
    e.preventDefault();
    try {
      if (newClient.website_url && !isValidHttpUrl(newClient.website_url)) {
        toast.error('Website URL must be a valid http(s) URL');
        return;
      }

      const payload = {
        name: newClient.name,
        website_url: newClient.website_url ? newClient.website_url : null,
        status: newClient.status,
        uses_content: Boolean(newClient.uses_content),
        uses_link_building: Boolean(newClient.uses_link_building),
        uses_digital_pr: Boolean(newClient.uses_digital_pr),
        content_monthly_target: Number(newClient.content_monthly_target || 0)
      };
      if (!payload.uses_content && !payload.uses_link_building && !payload.uses_digital_pr) {
        toast.error('Select at least one service');
        return;
      }
      if (!Number.isFinite(payload.content_monthly_target) || payload.content_monthly_target < 0) {
        toast.error('Content monthly target must be a valid non-negative number');
        return;
      }
      const monthlyTargetRaw = String(newClient.lb_monthly_target ?? '').trim();
      const monthlyTargetParsed = monthlyTargetRaw === '' ? null : Number(monthlyTargetRaw);
      if (!editingClient && monthlyTargetRaw !== '' && !Number.isFinite(monthlyTargetParsed)) {
        toast.error('Link Building monthly target must be a number');
        return;
      }
      const dprMonthlyTargetRaw = String(newClient.dpr_monthly_target ?? '').trim();
      const dprMonthlyTargetParsed = dprMonthlyTargetRaw === '' ? null : Number(dprMonthlyTargetRaw);
      if (!editingClient && dprMonthlyTargetRaw !== '' && !Number.isFinite(dprMonthlyTargetParsed)) {
        toast.error('Digital PR monthly target must be a number');
        return;
      }

      if (editingClient) {
        await axios.put(`/api/clients/${editingClient.id}`, payload);
        toast.success('Client updated successfully');
      } else {
        const response = await axios.post('/api/clients', payload);
        const createdClientId = Number(response?.data?.clientId);
        if (
          Number.isFinite(createdClientId) &&
          selectedLbTargetMonthId &&
          monthlyTargetParsed !== null &&
          Number.isFinite(monthlyTargetParsed)
        ) {
          await axios.post('/api/link-building/client-monthly-targets', {
            client_id: createdClientId,
            month_id: Number(selectedLbTargetMonthId),
            monthly_target: monthlyTargetParsed
          });
        }
        if (
          Number.isFinite(createdClientId) &&
          selectedDprTargetMonthId &&
          dprMonthlyTargetParsed !== null &&
          Number.isFinite(dprMonthlyTargetParsed)
        ) {
          await axios.post('/api/digital-pr/client-monthly-targets', {
            client_id: createdClientId,
            month_id: Number(selectedDprTargetMonthId),
            monthly_target: dprMonthlyTargetParsed
          });
        }
        toast.success('Client created successfully');
      }

      setShowClientForm(false);
      setEditingClient(null);
      setNewClient({
        name: '',
        website_url: '',
        status: 'active',
        lb_monthly_target: '',
        dpr_monthly_target: '',
        uses_content: true,
        uses_link_building: true,
        uses_digital_pr: false,
        content_monthly_target: ''
      });
      setSelectedLbTargetMonthId(getDefaultLbMonthId(lbMonths));
      setSelectedDprTargetMonthId(getDefaultLbMonthId(dprMonths));
      fetchData();
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to save client';
      toast.error(message);
    }
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setNewUser({
      username: user.username,
      full_name: user.full_name || '',
      email: user.email,
      password: '',
      role: user.role
    });
    setShowUserForm(true);
  };

  const openEditWeek = (week) => {
    setEditingWeek(week);
    setNewWeek({
      week_number: String(week.week_number),
      year: String(week.year),
      start_date: toDateInputValue(week.start_date),
      end_date: toDateInputValue(week.end_date)
    });
    setShowWeekForm(true);
  };

  const openEditTarget = (target) => {
    setEditingTarget(target);
    setNewTarget({
      user_id: String(target.user_id),
      week_id: String(target.week_id),
      client_id: target.client_id ? String(target.client_id) : '',
      title: target.title,
      client_url: target.client_url || '',
      description: target.description || '',
      keywordRows: pairsToFormRows(target.keyword, target.search_volume),
      status: target.status || 'pending',
      evidence: target.evidence || ''
    });
    setShowTargetForm(true);
  };

  const updateKeywordRow = (index, field, value) => {
    setNewTarget((prev) => {
      const rows = [...(prev.keywordRows || [emptyKeywordRow()])];
      const current = { ...(rows[index] || emptyKeywordRow()) };
      current[field] = value;
      if (field === 'keyword') {
        current.search_volume = '';
        current.loading = false;
      }
      rows[index] = current;
      return { ...prev, keywordRows: rows };
    });
  };

  const addKeywordRow = () => {
    setNewTarget((prev) => ({
      ...prev,
      keywordRows: [...(prev.keywordRows || []), emptyKeywordRow()]
    }));
  };

  const removeKeywordRow = (index) => {
    setNewTarget((prev) => {
      const rows = [...(prev.keywordRows || [])];
      rows.splice(index, 1);
      return { ...prev, keywordRows: rows.length > 0 ? rows : [emptyKeywordRow()] };
    });
  };

  const deleteUser = async () => {
    if (!editingUser) return;
    if (!window.confirm(`Delete user "${editingUser.username}"?`)) return;
    try {
      await axios.delete(`/api/users/${editingUser.id}`);
      toast.success('User deleted successfully');
      setShowUserForm(false);
      setEditingUser(null);
      setNewUser({ username: '', full_name: '', email: '', password: '', role: 'user' });
      fetchData();
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to delete user';
      toast.error(message);
    }
  };

  const deleteWeek = async () => {
    if (!editingWeek) return;
    if (!window.confirm(`Delete week ${editingWeek.week_number}, ${editingWeek.year}?`)) return;
    try {
      await axios.delete(`/api/weeks/${editingWeek.id}`);
      toast.success('Week deleted successfully');
      setShowWeekForm(false);
      setEditingWeek(null);
      setNewWeek({ week_number: '', year: '', start_date: '', end_date: '' });
      fetchData();
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to delete week';
      toast.error(message);
    }
  };

  const deleteTarget = async () => {
    if (!editingTarget) return;
    if (!window.confirm(`Delete target "${editingTarget.title}"?`)) return;
    try {
      await axios.delete(`/api/targets/${editingTarget.id}`);
      toast.success('Target deleted successfully');
      setShowTargetForm(false);
      setEditingTarget(null);
      setNewTarget(emptyTargetForm());
      fetchTargets();
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to delete target';
      toast.error(message);
    }
  };

  const openEditClient = (client) => {
    setEditingClient(client);
    setNewClient({
      name: client.name,
      website_url: client.website_url || '',
      status: client.status || 'active',
      lb_monthly_target: '',
      dpr_monthly_target: '',
      uses_content: client.uses_content !== undefined ? Boolean(client.uses_content) : true,
      uses_link_building: client.uses_link_building !== undefined ? Boolean(client.uses_link_building) : true,
      uses_digital_pr: client.uses_digital_pr !== undefined ? Boolean(client.uses_digital_pr) : false,
      content_monthly_target: String(Number(client.content_monthly_target || 0))
    });
    setSelectedLbTargetMonthId(getDefaultLbMonthId(lbMonths));
    setSelectedDprTargetMonthId(getDefaultLbMonthId(dprMonths));
    loadClientMonthlyTargets(client.id);
    loadClientDprMonthlyTargets(client.id);
    setShowClientForm(true);
  };

  const loadClientMonthlyTargets = async (clientId) => {
    try {
      const response = await axios.get(`/api/link-building/client-monthly-targets?client_id=${clientId}`);
      const rows = response.data || [];
      const draftMap = {};
      rows.forEach((row) => {
        draftMap[String(row.month_id)] = String(Number(row.monthly_target || 0));
      });
      setMonthlyTargetDrafts(draftMap);
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to load monthly targets';
      toast.error(message);
      setMonthlyTargetDrafts({});
    }
  };

  const loadClientDprMonthlyTargets = async (clientId) => {
    try {
      const response = await axios.get(`/api/digital-pr/client-monthly-targets?client_id=${clientId}`);
      const rows = response.data || [];
      const draftMap = {};
      rows.forEach((row) => {
        draftMap[String(row.month_id)] = String(Number(row.monthly_target || 0));
      });
      setDprMonthlyTargetDrafts(draftMap);
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to load Digital PR monthly targets';
      toast.error(message);
      setDprMonthlyTargetDrafts({});
    }
  };

  const saveClientMonthlyTarget = async (monthId) => {
    if (!editingClient) return;
    const value = monthlyTargetDrafts[String(monthId)] ?? '';
    const parsed = value === '' ? 0 : Number(value);
    if (!Number.isFinite(parsed)) {
      toast.error('Monthly target must be a number');
      return;
    }

    setSavingMonthlyTargetByMonthId((prev) => ({ ...prev, [String(monthId)]: true }));
    try {
      await axios.post('/api/link-building/client-monthly-targets', {
        client_id: Number(editingClient.id),
        month_id: Number(monthId),
        monthly_target: parsed
      });
      toast.success('Monthly target saved');
      await loadClientMonthlyTargets(editingClient.id);
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to save monthly target';
      toast.error(message);
    } finally {
      setSavingMonthlyTargetByMonthId((prev) => ({ ...prev, [String(monthId)]: false }));
    }
  };

  const saveClientDprMonthlyTarget = async (monthId) => {
    if (!editingClient) return;
    const value = dprMonthlyTargetDrafts[String(monthId)] ?? '';
    const parsed = value === '' ? 0 : Number(value);
    if (!Number.isFinite(parsed)) {
      toast.error('Digital PR monthly target must be a number');
      return;
    }

    setSavingDprMonthlyTargetByMonthId((prev) => ({ ...prev, [String(monthId)]: true }));
    try {
      await axios.post('/api/digital-pr/client-monthly-targets', {
        client_id: Number(editingClient.id),
        month_id: Number(monthId),
        monthly_target: parsed
      });
      toast.success('Digital PR monthly target saved');
      await loadClientDprMonthlyTargets(editingClient.id);
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to save Digital PR monthly target';
      toast.error(message);
    } finally {
      setSavingDprMonthlyTargetByMonthId((prev) => ({ ...prev, [String(monthId)]: false }));
    }
  };

  const deleteClient = async () => {
    if (!editingClient) return;
    if (!window.confirm(`Delete client "${editingClient.name}"?`)) return;
    try {
      await axios.delete(`/api/clients/${editingClient.id}`);
      toast.success('Client deleted successfully');
      setShowClientForm(false);
      setEditingClient(null);
      setNewClient({
        name: '',
        website_url: '',
        status: 'active',
        lb_monthly_target: '',
        dpr_monthly_target: '',
        uses_content: true,
        uses_link_building: true,
        uses_digital_pr: false,
        content_monthly_target: ''
      });
      setMonthlyTargetDrafts({});
      setDprMonthlyTargetDrafts({});
      setSelectedLbTargetMonthId(getDefaultLbMonthId(lbMonths));
      setSelectedDprTargetMonthId(getDefaultLbMonthId(dprMonths));
      fetchData();
      fetchTargets();
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to delete client';
      toast.error(message);
    }
  };

  const importTargets = async (e) => {
    e.preventDefault();
    if (!importFile) {
      toast.error('Please choose a CSV file');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const response = await axios.post('/api/targets/import', formData);
      const {
        created = 0,
        updatedExisting = 0,
        deduped = 0,
        skipped = 0,
        failed = 0
      } = response.data || {};
      toast.success(
        `Import complete. Created: ${created}, Updated: ${updatedExisting}, Duplicates removed: ${deduped}, Skipped: ${skipped}, Failed: ${failed}`
      );
      setShowImportForm(false);
      setImportFile(null);
      fetchTargets();
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to import targets';
      toast.error(message);
    }
  };

  const importLinkBuildingTargets = async (e) => {
    e.preventDefault();
    if (!lbImportFile) {
      toast.error('Please choose a CSV file');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', lbImportFile);
      const response = await axios.post('/api/link-building/content/import', formData);
      const {
        inserted = 0,
        updatedExisting = 0,
        skipped = 0,
        failed = 0,
        deduped = 0,
        errors = []
      } = response.data || {};
      toast.success(
        `Import complete. Inserted: ${inserted}, Updated: ${updatedExisting}, Duplicates removed: ${deduped}, Skipped: ${skipped}, Failed: ${failed}`
      );
      setShowLbImportForm(false);
      setLbImportFile(null);
      setLbImportResults({ inserted, updatedExisting, skipped, failed, deduped, errors });
      setShowLbImportResults(true);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to import link-building targets';
      toast.error(message);
    }
  };

  const importDigitalPrTargets = async (e) => {
    e.preventDefault();
    if (!dprImportFile) {
      toast.error('Please choose a CSV file');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', dprImportFile);
      const response = await axios.post('/api/digital-pr/content/import', formData);
      const {
        inserted = 0,
        updatedExisting = 0,
        skipped = 0,
        failed = 0,
        deduped = 0,
        errors = []
      } = response.data || {};
      toast.success(
        `Import complete. Inserted: ${inserted}, Updated: ${updatedExisting}, Duplicates removed: ${deduped}, Skipped: ${skipped}, Failed: ${failed}`
      );
      setShowDprImportForm(false);
      setDprImportFile(null);
      setDprImportResults({ inserted, updatedExisting, skipped, failed, deduped, errors });
      setShowDprImportResults(true);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to import Digital PR targets';
      toast.error(message);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-success-500" />;
      case 'in_progress':
        return <Clock className="h-5 w-5 text-warning-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-success-100 text-success-800';
      case 'in_progress':
        return 'bg-warning-100 text-warning-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const totalTargets = targets.length;
  const completedTargets = targets.filter(t => t.status === 'completed').length;

  const targetsByClient = useMemo(() => {
    const groups = new Map();
    targets.forEach((target) => {
      const key = target.client_id ? String(target.client_id) : 'none';
      const label = target.client_id ? (target.client_name || `Client ${target.client_id}`) : 'No client';
      if (!groups.has(key)) {
        groups.set(key, { key, label, targets: [] });
      }
      groups.get(key).targets.push(target);
    });

    return Array.from(groups.values()).sort((a, b) => {
      if (a.key === 'none') return 1;
      if (b.key === 'none') return -1;
      return a.label.localeCompare(b.label);
    });
  }, [targets]);

  const filteredClients = useMemo(() => {
    if (showInactiveClients) return clients;
    return clients.filter((client) => String(client.status || '').toLowerCase() !== 'inactive');
  }, [clients, showInactiveClients]);

  const lbTargetsByClientId = useMemo(() => {
    const monthId = selectedLbTargetMonthId || getDefaultLbMonthId(lbMonths);
    const map = new Map();
    allLbClientMonthlyTargets.forEach((row) => {
      if (monthId && String(row.month_id) === String(monthId)) {
        map.set(String(row.client_id), Number(row.monthly_target || 0));
      }
    });
    return map;
  }, [allLbClientMonthlyTargets, selectedLbTargetMonthId, lbMonths]);

  const lbTargetsForSelectedMonth = useMemo(() => {
    return lbContentRows.filter((row) => {
      if (!selectedLbTargetsMonthId) return true;
      return String(row.month_id) === String(selectedLbTargetsMonthId);
    });
  }, [lbContentRows, selectedLbTargetsMonthId]);

  const selectedLbTargetsMonthValue = useMemo(() => {
    if (!selectedLbTargetsMonthId) return '';
    const selected = lbMonths.find((month) => String(month.id) === String(selectedLbTargetsMonthId));
    return selected?.month_label || '';
  }, [lbMonths, selectedLbTargetsMonthId]);

  const lbTargetsByClient = useMemo(() => {
    const map = new Map();
    lbTargetsForSelectedMonth.forEach((row) => {
      const key = row.client_id ? String(row.client_id) : 'none';
      const label = row.client_name || 'No client';
      if (!map.has(key)) {
        map.set(key, { key, label, rows: [] });
      }
      map.get(key).rows.push(row);
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [lbTargetsForSelectedMonth]);

  const dprTargetsByClientId = useMemo(() => {
    const monthId = selectedDprTargetMonthId || getDefaultLbMonthId(dprMonths);
    const map = new Map();
    allDprClientMonthlyTargets.forEach((row) => {
      if (monthId && String(row.month_id) === String(monthId)) {
        map.set(String(row.client_id), Number(row.monthly_target || 0));
      }
    });
    return map;
  }, [allDprClientMonthlyTargets, selectedDprTargetMonthId, dprMonths]);

  const dprTargetsForSelectedMonth = useMemo(() => {
    return dprContentRows.filter((row) => {
      if (!selectedDprTargetsMonthId) return true;
      return String(row.month_id) === String(selectedDprTargetsMonthId);
    });
  }, [dprContentRows, selectedDprTargetsMonthId]);

  const selectedDprTargetsMonthValue = useMemo(() => {
    if (!selectedDprTargetsMonthId) return '';
    const selected = dprMonths.find((month) => String(month.id) === String(selectedDprTargetsMonthId));
    return selected?.month_label || '';
  }, [dprMonths, selectedDprTargetsMonthId]);

  const dprTargetsByClient = useMemo(() => {
    const map = new Map();
    dprTargetsForSelectedMonth.forEach((row) => {
      const key = row.client_id ? String(row.client_id) : 'none';
      const label = row.client_name || 'No client';
      if (!map.has(key)) {
        map.set(key, { key, label, rows: [] });
      }
      map.get(key).rows.push(row);
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [dprTargetsForSelectedMonth]);

  const userPerformanceRows = useMemo(() => {
    const currentWeekId = getCurrentWeekId(weeks);
    const nowMs = Date.now();
    const dayMs = 24 * 60 * 60 * 1000;
    const fromMs =
      performanceDateRange === '7d'
        ? nowMs - (7 * dayMs)
        : performanceDateRange === '30d'
          ? nowMs - (30 * dayMs)
          : null;

    const scopedTargets = allTargets.filter((target) => {
      if (performanceScope === 'all_time') return true;
      if (performanceScope === 'selected_week') {
        return selectedWeek ? Number(target.week_id) === Number(selectedWeek.id) : false;
      }
      if (performanceScope === 'current_week') {
        return currentWeekId ? Number(target.week_id) === Number(currentWeekId) : false;
      }
      return true;
    });
    const datedTargets = scopedTargets.filter((target) => {
      if (fromMs === null) return true;
      const stamp = target.completed_at || target.started_at || target.created_at;
      if (!stamp) return false;
      const ts = new Date(stamp).getTime();
      return !Number.isNaN(ts) && ts >= fromMs;
    });

    const durationFromTarget = (target) => {
      const direct = Number(target.duration_seconds);
      if (Number.isFinite(direct) && direct >= 0) return direct;
      if (target.started_at && target.completed_at) {
        const start = new Date(target.started_at).getTime();
        const end = new Date(target.completed_at).getTime();
        if (!Number.isNaN(start) && !Number.isNaN(end) && end >= start) {
          return Math.round((end - start) / 1000);
        }
      }
      return null;
    };

    return users
      .filter((user) => user.role !== 'admin')
      .map((user) => {
        const mine = datedTargets.filter((target) => Number(target.user_id) === Number(user.id));
        const completed = mine.filter((target) => target.status === 'completed');
        const inProgress = mine.filter((target) => target.status === 'in_progress');
        const durations = completed
          .map(durationFromTarget)
          .filter((value) => Number.isFinite(value));
        const avgDurationSeconds = durations.length > 0
          ? Math.round(durations.reduce((sum, value) => sum + value, 0) / durations.length)
          : null;

        return {
          userId: user.id,
          name: user.full_name || user.username,
          role: user.role,
          totalAssigned: mine.length,
          completedCount: completed.length,
          inProgressCount: inProgress.length,
          avgDurationSeconds
        };
      })
      .sort((a, b) => {
        if (b.completedCount !== a.completedCount) return b.completedCount - a.completedCount;
        return a.name.localeCompare(b.name);
      });
  }, [users, allTargets, weeks, selectedWeek, performanceScope, performanceDateRange]);

  const performanceSummary = useMemo(() => {
    if (userPerformanceRows.length === 0) {
      return { topCompleted: null, fastest: null, busiestInProgress: null };
    }
    const topCompleted = [...userPerformanceRows].sort((a, b) => b.completedCount - a.completedCount)[0] || null;
    const fastest = [...userPerformanceRows]
      .filter((row) => row.avgDurationSeconds !== null)
      .sort((a, b) => a.avgDurationSeconds - b.avgDurationSeconds)[0] || null;
    const busiestInProgress = [...userPerformanceRows].sort((a, b) => b.inProgressCount - a.inProgressCount)[0] || null;
    return { topCompleted, fastest, busiestInProgress };
  }, [userPerformanceRows]);

  const performanceScopeSummary = useMemo(() => {
    const targetCount = userPerformanceRows.reduce((sum, row) => sum + row.totalAssigned, 0);
    const userCount = userPerformanceRows.length;
    const activeUserCount = userPerformanceRows.filter(
      (row) => row.totalAssigned > 0 || row.inProgressCount > 0 || row.completedCount > 0
    ).length;
    return { targetCount, userCount, activeUserCount };
  }, [userPerformanceRows]);

  const exportPerformanceCsv = () => {
    const scopeLabelMap = {
      all_time: 'All Time',
      selected_week: selectedWeek ? `Selected Week (${selectedWeek.week_number}, ${selectedWeek.year})` : 'Selected Week',
      current_week: 'Current Week'
    };
    const dateLabelMap = {
      all: 'All Dates',
      '7d': 'Last 7 Days',
      '30d': 'Last 30 Days'
    };
    const header = [
      `Scope: ${scopeLabelMap[performanceScope] || performanceScope}`,
      `Date Range: ${dateLabelMap[performanceDateRange] || performanceDateRange}`,
      `Exported At: ${new Date().toISOString()}`
    ].join(',');

    const columns = ['User', 'Role', 'Assigned', 'Completed', 'In Progress', 'Avg Completion Seconds'];
    const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const rows = userPerformanceRows.map((row) => [
      row.name,
      row.role,
      row.totalAssigned,
      row.completedCount,
      row.inProgressCount,
      row.avgDurationSeconds ?? ''
    ].map(escapeCsv).join(','));

    const csv = [header, columns.map(escapeCsv).join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `user-performance-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const reportUserOptions = useMemo(() => {
    return users
      .filter((u) => u.role !== 'admin')
      .map((u) => ({
        id: String(u.id),
        name: u.full_name || u.username
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [users]);

  const reportClientOptions = useMemo(() => {
    return clients
      .map((c) => ({ id: String(c.id), name: c.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [clients]);

  const frequencyClientOptions = useMemo(() => {
    return clients
      .filter((c) => c.uses_link_building !== 0 && c.uses_link_building !== false)
      .map((c) => ({ id: String(c.id), name: c.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [clients]);

  const frequencyClientName = useMemo(() => {
    if (!frequencyAppliedFilters.clientId) return '';
    const match = frequencyClientOptions.find(
      (client) => String(client.id) === String(frequencyAppliedFilters.clientId)
    );
    return match?.name || 'Selected client';
  }, [frequencyAppliedFilters.clientId, frequencyClientOptions]);

  const runWebsiteFrequencyReport = () => {
    if (!frequencyClientId) {
      toast.error('Select a client first');
      return;
    }
    setFrequencyAppliedFilters({
      clientId: frequencyClientId,
      dateFrom: frequencyDateFrom,
      dateTo: frequencyDateTo
    });
    setFrequencyReportActive(true);
    setExpandedFrequencyDomain('');
  };

  const reportRows = useMemo(() => {
    const contentRows = allTargets.map((target) => {
      const owner = users.find((u) => Number(u.id) === Number(target.user_id));
      const assigneeName = owner?.full_name || owner?.username || target.username || `User ${target.user_id}`;
      const eventDate = target.completed_at || target.started_at || target.created_at || null;
      return {
        service: 'content',
        serviceLabel: 'Content',
        itemId: `content-${target.id}`,
        eventDate,
        clientId: target.client_id ? String(target.client_id) : '',
        clientName: target.client_name || 'No client',
        userId: target.user_id ? String(target.user_id) : '',
        userName: assigneeName,
        title: target.title || '',
        status: target.status || 'pending',
        amountGbp: 0
      };
    });

    const linkBuildingRows = lbContentRows.map((row) => ({
      service: 'link_building',
      serviceLabel: 'Link Building',
      itemId: `lb-${row.id}`,
      eventDate: row.month_start || row.created_at || row.updated_at || null,
      clientId: row.client_id ? String(row.client_id) : '',
      clientName: row.client_name || 'No client',
      userId: row.writer_id ? String(row.writer_id) : '',
      userName: row.writer_name_override || row.writer_full_name || row.writer_username || 'Unassigned',
      title: row.article_title || '',
      status: row.is_live ? 'live' : row.is_approved ? 'sent_for_approval' : row.qc ? 'qc' : 'pending',
      amountGbp: Number(row.price_gbp || row.price || 0)
    }));

    const digitalPrRows = dprContentRows.map((row) => ({
      service: 'digital_pr',
      serviceLabel: 'Digital PR',
      itemId: `dpr-${row.id}`,
      eventDate: row.month_start || row.created_at || row.updated_at || null,
      clientId: row.client_id ? String(row.client_id) : '',
      clientName: row.client_name || 'No client',
      userId: row.writer_id ? String(row.writer_id) : '',
      userName: row.writer_name_override || row.writer_full_name || row.writer_username || 'Unassigned',
      title: row.article_title || '',
      status: row.is_live ? 'live' : row.is_approved ? 'sent_for_approval' : row.qc ? 'qc' : 'pending',
      amountGbp: Number(row.price_gbp || row.price || 0)
    }));

    return [...contentRows, ...linkBuildingRows, ...digitalPrRows];
  }, [allTargets, lbContentRows, dprContentRows, users]);

  const reportFilteredRows = useMemo(() => {
    const fromMs = reportDateFrom ? new Date(`${reportDateFrom}T00:00:00`).getTime() : null;
    const toMs = reportDateTo ? new Date(`${reportDateTo}T23:59:59`).getTime() : null;
    return reportRows.filter((row) => {
      if (reportService !== 'all' && row.service !== reportService) return false;
      if (reportClientId && String(row.clientId) !== String(reportClientId)) return false;
      if (reportUserId && String(row.userId) !== String(reportUserId)) return false;
      if (fromMs !== null || toMs !== null) {
        if (!row.eventDate) return false;
        const ts = new Date(row.eventDate).getTime();
        if (Number.isNaN(ts)) return false;
        if (fromMs !== null && ts < fromMs) return false;
        if (toMs !== null && ts > toMs) return false;
      }
      return true;
    });
  }, [reportRows, reportDateFrom, reportDateTo, reportClientId, reportUserId, reportService]);

  const reportingSummary = useMemo(() => {
    const contentCount = reportFilteredRows.filter((r) => r.service === 'content').length;
    const linkBuildingCount = reportFilteredRows.filter((r) => r.service === 'link_building').length;
    const digitalPrCount = reportFilteredRows.filter((r) => r.service === 'digital_pr').length;
    const totalCostGbp = reportFilteredRows.reduce((sum, row) => sum + Number(row.amountGbp || 0), 0);
    const clientCount = new Set(reportFilteredRows.map((r) => r.clientId).filter(Boolean)).size;
    const userCount = new Set(reportFilteredRows.map((r) => r.userId).filter(Boolean)).size;
    return {
      totalItems: reportFilteredRows.length,
      contentCount,
      linkBuildingCount,
      digitalPrCount,
      totalCostGbp,
      clientCount,
      userCount
    };
  }, [reportFilteredRows]);

  const reportByClient = useMemo(() => {
    const map = new Map();
    reportFilteredRows.forEach((row) => {
      const key = row.clientId || 'none';
      if (!map.has(key)) {
        map.set(key, {
          clientId: key,
          clientName: row.clientName || 'No client',
          contentCount: 0,
          linkBuildingCount: 0,
          digitalPrCount: 0,
          totalItems: 0,
          totalCostGbp: 0
        });
      }
      const bucket = map.get(key);
      bucket.totalItems += 1;
      if (row.service === 'content') bucket.contentCount += 1;
      if (row.service === 'link_building') bucket.linkBuildingCount += 1;
      if (row.service === 'digital_pr') bucket.digitalPrCount += 1;
      bucket.totalCostGbp += Number(row.amountGbp || 0);
    });
    return Array.from(map.values()).sort((a, b) => b.totalItems - a.totalItems || a.clientName.localeCompare(b.clientName));
  }, [reportFilteredRows]);

  const reportByUser = useMemo(() => {
    const map = new Map();
    reportFilteredRows.forEach((row) => {
      const key = row.userId || row.userName || 'unassigned';
      if (!map.has(key)) {
        map.set(key, {
          userId: key,
          userName: row.userName || 'Unassigned',
          contentCount: 0,
          linkBuildingCount: 0,
          digitalPrCount: 0,
          totalItems: 0,
          totalCostGbp: 0
        });
      }
      const bucket = map.get(key);
      bucket.totalItems += 1;
      if (row.service === 'content') bucket.contentCount += 1;
      if (row.service === 'link_building') bucket.linkBuildingCount += 1;
      if (row.service === 'digital_pr') bucket.digitalPrCount += 1;
      bucket.totalCostGbp += Number(row.amountGbp || 0);
    });
    return Array.from(map.values()).sort((a, b) => b.totalItems - a.totalItems || a.userName.localeCompare(b.userName));
  }, [reportFilteredRows]);

  const reportFinancialRows = useMemo(() => {
    const contentRows = reportFilteredRows.filter((r) => r.service === 'content');
    const lbRows = reportFilteredRows.filter((r) => r.service === 'link_building');
    const dprRows = reportFilteredRows.filter((r) => r.service === 'digital_pr');
    const lbCost = lbRows.reduce((sum, r) => sum + Number(r.amountGbp || 0), 0);
    const dprCost = dprRows.reduce((sum, r) => sum + Number(r.amountGbp || 0), 0);
    return [
      {
        service: 'Content',
        items: contentRows.length,
        totalCostGbp: 0,
        avgCostGbp: 0
      },
      {
        service: 'Link Building',
        items: lbRows.length,
        totalCostGbp: lbCost,
        avgCostGbp: lbRows.length > 0 ? lbCost / lbRows.length : 0
      },
      {
        service: 'Digital PR',
        items: dprRows.length,
        totalCostGbp: dprCost,
        avgCostGbp: dprRows.length > 0 ? dprCost / dprRows.length : 0
      }
    ];
  }, [reportFilteredRows]);

  const websiteFrequencyRows = useMemo(() => {
    if (!frequencyReportActive || !frequencyAppliedFilters.clientId) {
      return [];
    }

    const fromMs = frequencyAppliedFilters.dateFrom
      ? new Date(`${frequencyAppliedFilters.dateFrom}T00:00:00`).getTime()
      : null;
    const toMs = frequencyAppliedFilters.dateTo
      ? new Date(`${frequencyAppliedFilters.dateTo}T23:59:59`).getTime()
      : null;
    const grouped = new Map();

    lbContentRows.forEach((row) => {
      if (String(row.client_id || '') !== String(frequencyAppliedFilters.clientId)) return;

      const dateSource = row.created_at || row.updated_at || row.month_start || null;
      if (!dateSource) return;
      const ts = new Date(dateSource).getTime();
      if (Number.isNaN(ts)) return;
      if (fromMs !== null && ts < fromMs) return;
      if (toMs !== null && ts > toMs) return;

      const websiteUrl = String(row.website_url || '').trim();
      if (!websiteUrl) return;

      const rootDomain = extractRootDomain(websiteUrl);
      if (!rootDomain) return;

      if (!grouped.has(rootDomain)) {
        grouped.set(rootDomain, {
          rootDomain,
          placements: 0,
          distinctUrlsSet: new Set(),
          firstPlacementDate: new Date(ts).toISOString().slice(0, 10),
          lastPlacementDate: new Date(ts).toISOString().slice(0, 10)
        });
      }

      const bucket = grouped.get(rootDomain);
      const placementDate = new Date(ts).toISOString().slice(0, 10);
      bucket.placements += 1;
      bucket.distinctUrlsSet.add(websiteUrl);
      if (!bucket.firstPlacementDate || placementDate < bucket.firstPlacementDate) {
        bucket.firstPlacementDate = placementDate;
      }
      if (!bucket.lastPlacementDate || placementDate > bucket.lastPlacementDate) {
        bucket.lastPlacementDate = placementDate;
      }
    });

    return Array.from(grouped.values())
      .map((row) => ({
        rootDomain: row.rootDomain,
        placements: row.placements,
        distinctUrls: Array.from(row.distinctUrlsSet).sort((a, b) => a.localeCompare(b)),
        distinctUrlsCount: row.distinctUrlsSet.size,
        firstPlacementDate: row.firstPlacementDate,
        lastPlacementDate: row.lastPlacementDate
      }))
      .sort((a, b) => b.placements - a.placements || a.rootDomain.localeCompare(b.rootDomain));
  }, [lbContentRows, frequencyReportActive, frequencyAppliedFilters]);

  const exportCsv = (filename, columns, rows) => {
    const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const content = [
      columns.map(escapeCsv).join(','),
      ...rows.map((row) => row.map(escapeCsv).join(','))
    ].join('\n');
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const addDigitalPrMonth = async () => {
    const monthLabel = selectedDprTargetsMonthValue || getCurrentMonthLabel();
    const [yearRaw, monthRaw] = String(monthLabel).split('-');
    const year = Number(yearRaw);
    const month = Number(monthRaw);
    if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
      toast.error('Pick a valid month first');
      return;
    }
    const monthStart = `${year}-${String(month).padStart(2, '0')}-01`;
    try {
      const response = await axios.post('/api/digital-pr/months', { month_start: monthStart });
      toast.success('Digital PR month added');
      await fetchData();
      if (response?.data?.id) {
        setSelectedDprTargetsMonthId(String(response.data.id));
      }
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to add Digital PR month';
      toast.error(message);
    }
  };

  const addLinkBuildingMonth = async () => {
    const monthLabel = selectedLbTargetsMonthValue || getCurrentMonthLabel();
    if (!monthLabel || !/^\d{4}-\d{2}$/.test(monthLabel)) {
      toast.error('Select a valid month first');
      return;
    }

    try {
      const monthStart = `${monthLabel}-01`;
      const response = await axios.post('/api/link-building/months', { month_start: monthStart });
      const createdMonthId = response?.data?.monthId;
      toast.success('Month added successfully');
      await fetchData();
      if (createdMonthId) {
        setSelectedLbTargetsMonthId(String(createdMonthId));
      }
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to add month';
      toast.error(message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600">Manage users, weeks, clients, and targets</p>
        </div>
        {activeTab !== 'lb_targets' && activeTab !== 'dpr_targets' && activeTab !== 'reporting' && (
          <div className="flex flex-wrap items-center gap-2">
            <Calendar className="h-5 w-5 text-gray-400 shrink-0" />
            <label className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
              <span className="whitespace-nowrap">Jump to week</span>
              <input
                type="date"
                value={headerWeekDateInputValue}
                onChange={(e) => {
                  const picked = e.target.value;
                  const week = findWeekContainingDate(weeks, picked);
                  if (!week) {
                    toast.error('No week covers that date — pick a day inside an existing week range');
                    return;
                  }
                  setSelectedWeek(week);
                }}
                className="input w-auto max-w-[11rem]"
              />
            </label>
            {selectedWeek && (
              <span className="text-sm text-gray-600 whitespace-nowrap">
                Week {selectedWeek.week_number}, {selectedWeek.year}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', name: 'Overview', icon: Globe2 },
            ...(isAdmin ? [{ id: 'users', name: 'Users', icon: Users }] : []),
            { id: 'weeks', name: 'Weeks', icon: Calendar },
            { id: 'clients', name: 'Clients', icon: Briefcase },
            { id: 'targets', name: 'Content Targets', icon: Target },
            { id: 'lb_targets', name: 'Link Building Targets', icon: Paperclip },
            { id: 'dpr_targets', name: 'Digital PR Targets', icon: Megaphone },
            { id: 'reporting', name: 'Reporting', icon: BarChart3 }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-5 w-5" />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Users className="h-8 w-8 text-primary-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Users</dt>
                    <dd className="text-lg font-medium text-gray-900">{users.length}</dd>
                  </dl>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Calendar className="h-8 w-8 text-primary-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Weeks</dt>
                    <dd className="text-lg font-medium text-gray-900">{weeks.length}</dd>
                  </dl>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Target className="h-8 w-8 text-primary-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Targets</dt>
                    <dd className="text-lg font-medium text-gray-900">{totalTargets}</dd>
                  </dl>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircle className="h-8 w-8 text-success-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Completed</dt>
                    <dd className="text-lg font-medium text-gray-900">{completedTargets}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between mb-4 gap-3">
              <h2 className="text-lg font-medium text-gray-900">User Performance</h2>
              <div className="flex items-center gap-2">
                <select
                  value={performanceScope}
                  onChange={(e) => setPerformanceScope(e.target.value)}
                  className="input max-w-xs"
                >
                  <option value="all_time">All Time</option>
                  <option value="selected_week">Selected Week</option>
                  <option value="current_week">Current Week</option>
                </select>
                <select
                  value={performanceDateRange}
                  onChange={(e) => setPerformanceDateRange(e.target.value)}
                  className="input max-w-xs"
                >
                  <option value="all">All Dates</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                </select>
                <button
                  type="button"
                  className="btn btn-secondary inline-flex items-center h-10 px-4 whitespace-nowrap"
                  onClick={exportPerformanceCsv}
                  disabled={userPerformanceRows.length === 0}
                >
                  Export CSV
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                <p className="text-xs uppercase text-gray-500">Highest Completed</p>
                <p className="text-sm font-semibold text-gray-900">
                  {performanceSummary.topCompleted ? `${performanceSummary.topCompleted.name} (${performanceSummary.topCompleted.completedCount})` : '—'}
                </p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                <p className="text-xs uppercase text-gray-500">Fastest Avg Completion</p>
                <p className="text-sm font-semibold text-gray-900">
                  {performanceSummary.fastest ? `${performanceSummary.fastest.name} (${formatDuration(performanceSummary.fastest.avgDurationSeconds)})` : '—'}
                </p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                <p className="text-xs uppercase text-gray-500">Most In Progress</p>
                <p className="text-sm font-semibold text-gray-900">
                  {performanceSummary.busiestInProgress ? `${performanceSummary.busiestInProgress.name} (${performanceSummary.busiestInProgress.inProgressCount})` : '—'}
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              In scope: {performanceScopeSummary.targetCount} targets across {performanceScopeSummary.activeUserCount} active users
              ({performanceScopeSummary.userCount} total users in report).
            </p>
            {userPerformanceRows.length === 0 ? (
              <p className="text-sm text-gray-500">No users available for performance metrics.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Completed</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">In Progress</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Completion</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {userPerformanceRows.map((row) => (
                      <tr key={row.userId}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{row.role}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 text-right">{row.totalAssigned}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 text-right">{row.completedCount}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 text-right">{row.inProgressCount}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 text-right">
                          {row.avgDurationSeconds !== null ? formatDuration(row.avgDurationSeconds) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">Users</h2>
            <button
              onClick={() => {
                setEditingUser(null);
                setNewUser({ username: '', full_name: '', email: '', password: '', role: 'user' });
                setShowUserForm(true);
              }}
              className="btn btn-primary flex items-center"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Add User
            </button>
          </div>

          <div className="card">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{user.username}</div>
                          {user.full_name && (
                            <div className="text-sm text-gray-500">{user.full_name}</div>
                          )}
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.role === 'admin'
                            ? 'bg-purple-100 text-purple-800'
                            : user.role === 'link_manager'
                              ? 'bg-cyan-100 text-cyan-800'
                              : user.role === 'compliance'
                                ? 'bg-amber-100 text-amber-800'
                              : 'bg-gray-100 text-gray-800'
                        }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <button
                          onClick={() => openEditUser(user)}
                          className="btn btn-secondary inline-flex items-center"
                        >
                          <Pencil className="h-4 w-4 mr-1" />
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Weeks Tab */}
      {activeTab === 'weeks' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">Weeks</h2>
            <button
              onClick={() => {
                setEditingWeek(null);
                setNewWeek({ week_number: '', year: '', start_date: '', end_date: '' });
                setShowWeekForm(true);
              }}
              className="btn btn-primary flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Week
            </button>
          </div>

          <div className="card">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Week</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Year</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {weeks.map((week) => (
                    <tr key={week.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        Week {week.week_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{week.year}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{week.start_date}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{week.end_date}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <button
                          onClick={() => openEditWeek(week)}
                          className="btn btn-secondary inline-flex items-center"
                        >
                          <Pencil className="h-4 w-4 mr-1" />
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Clients Tab */}
      {activeTab === 'clients' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">Clients</h2>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={showInactiveClients}
                  onChange={(e) => setShowInactiveClients(e.target.checked)}
                />
                Show inactive
              </label>
              <button
                onClick={() => {
                  setEditingClient(null);
                  setNewClient({
                    name: '',
                    website_url: '',
                    status: 'active',
                    lb_monthly_target: '',
                    dpr_monthly_target: '',
                    uses_content: true,
                    uses_link_building: true,
                    uses_digital_pr: false,
                    content_monthly_target: ''
                  });
                  setSelectedLbTargetMonthId(getDefaultLbMonthId(lbMonths));
                  setSelectedDprTargetMonthId(getDefaultLbMonthId(dprMonths));
                  setMonthlyTargetDrafts({});
                  setDprMonthlyTargetDrafts({});
                  setShowClientForm(true);
                }}
                className="btn btn-primary flex items-center"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Client
              </button>
            </div>
          </div>

          <div className="card">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Website</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Services</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Content Target</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Link Building Target</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Digital PR Target</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredClients.map((client) => (
                    <tr key={client.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{client.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {client.website_url ? (
                          isValidHttpUrl(client.website_url) ? (
                            <a
                              href={client.website_url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary-600 hover:text-primary-700 underline"
                            >
                              {client.website_url}
                            </a>
                          ) : (
                            client.website_url
                          )
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          client.status === 'active' ? 'bg-success-100 text-success-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {client.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex flex-wrap gap-1">
                          {Boolean(client.uses_content) && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Content
                            </span>
                          )}
                          {Boolean(client.uses_link_building) && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800">
                              Link Building
                            </span>
                          )}
                          {Boolean(client.uses_digital_pr) && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                              Digital PR
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {Number(client.content_monthly_target || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {Number(lbTargetsByClientId.get(String(client.id)) || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {Number(dprTargetsByClientId.get(String(client.id)) || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <button
                          onClick={() => openEditClient(client)}
                          className="btn btn-secondary inline-flex items-center"
                        >
                          <Pencil className="h-4 w-4 mr-1" />
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Targets Tab */}
      {activeTab === 'targets' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">Content Targets</h2>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => {
                  setImportFile(null);
                  setShowImportForm(true);
                }}
                className="btn btn-secondary flex items-center"
              >
                Import CSV
              </button>
              <button
                onClick={() => {
                  setEditingTarget(null);
                  setNewTarget(emptyTargetForm(selectedWeek ? String(selectedWeek.id) : ''));
                  setShowTargetForm(true);
                }}
                className="btn btn-primary flex items-center"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Target
              </button>
            </div>
          </div>

          <div className="card">
            <div className="space-y-4">
              {targets.length === 0 ? (
                <div className="text-center py-8">
                  <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No targets for this week</p>
                </div>
              ) : (
                targetsByClient.map((group) => (
                  <details key={group.key} className="border border-gray-200 rounded-lg">
                    <summary className="cursor-pointer select-none px-4 py-3 flex items-center justify-between bg-gray-50 rounded-t-lg">
                      <div className="flex items-center space-x-2">
                        <Briefcase className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-gray-900">{group.label}</span>
                      </div>
                      <span className="text-sm text-gray-500">{group.targets.length}</span>
                    </summary>
                    <div className="p-4 space-y-4">
                      {group.targets.map((target) => (
                        <div key={target.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                {getStatusIcon(target.status)}
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(target.status)}`}>
                                  {target.status.replace('_', ' ')}
                                </span>
                              </div>
                              <h3 className="text-lg font-medium text-gray-900 mb-2">{target.title}</h3>
                              {target.description && (
                                <p className="text-gray-600 mb-2">
                                  <strong>Notes:</strong> {target.description}
                                </p>
                              )}
                              <p className="text-sm text-gray-500">Assigned to: {target.username}</p>
                              {target.keyword && (
                                <p className="text-sm text-gray-600 mt-1">
                                  <strong>Keywords:</strong>{' '}
                                  {formatKeywordPairsForDisplay(target.keyword, target.search_volume)}
                                </p>
                              )}
                              {target.evidence && (
                                <p className="text-sm text-gray-600 mt-2">
                                  <strong>Evidence:</strong>{' '}
                                  {isValidHttpUrl(target.evidence) ? (
                                    <a
                                      href={target.evidence}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-primary-600 hover:text-primary-700 underline"
                                    >
                                      {target.evidence}
                                    </a>
                                  ) : (
                                    target.evidence
                                  )}
                                </p>
                              )}
                            </div>
                            <button
                              onClick={() => openEditTarget(target)}
                              className="btn btn-secondary inline-flex items-center ml-4"
                            >
                              <Pencil className="h-4 w-4 mr-1" />
                              Edit
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </details>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'lb_targets' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">Link Building Targets</h2>
            <div className="flex items-center space-x-3">
              <input
                type="month"
                value={selectedLbTargetsMonthValue}
                onChange={(e) => {
                  const selectedLabel = e.target.value;
                  const selected = lbMonths.find((month) => month.month_label === selectedLabel);
                  setSelectedLbTargetsMonthId(selected ? String(selected.id) : '');
                }}
                className="input max-w-xs"
              />
              <button
                onClick={addLinkBuildingMonth}
                className="btn btn-secondary inline-flex items-center h-10 px-4 whitespace-nowrap"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Month
              </button>
              <button
                onClick={() => {
                  setLbImportFile(null);
                      setLbImportResults(null);
                      setShowLbImportResults(false);
                  setShowLbImportForm(true);
                }}
                className="btn btn-primary inline-flex items-center gap-2 h-10 px-4 whitespace-nowrap"
              >
                <Plus className="h-4 w-4 mr-2" />
                Import Link Building CSV
              </button>
            </div>
          </div>

          <div className="card">
            <div className="space-y-4">
              {lbTargetsForSelectedMonth.length === 0 ? (
                <div className="text-center py-8">
                  <Briefcase className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No link-building targets for this month</p>
                </div>
              ) : (
                lbTargetsByClient.map((group) => (
                  <details key={group.key} className="border border-gray-200 rounded-lg">
                    <summary className="cursor-pointer select-none px-4 py-3 flex items-center justify-between bg-gray-50 rounded-t-lg">
                      <div className="flex items-center space-x-2">
                        <Briefcase className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-gray-900">{group.label}</span>
                      </div>
                      <span className="text-sm text-gray-500">{group.rows.length}</span>
                    </summary>
                    <div className="p-4 space-y-3">
                      {group.rows.map((row) => (
                        <div key={row.id} className="border border-gray-200 rounded-lg p-3">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-gray-900">{row.article_title || 'Untitled'}</p>
                              <p className="text-sm text-gray-500">
                                Writer: {row.writer_name_override || row.writer_full_name || row.writer_username || '—'}
                              </p>
                              <p className="text-sm text-gray-500">
                                Month: {row.month_start ? String(row.month_start).slice(0, 7) : '—'}
                              </p>
                            </div>
                            <span className="text-sm text-gray-600">
                              {row.currency_code || 'GBP'} {Number(row.price || 0).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </details>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'dpr_targets' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">Digital PR Targets</h2>
            <div className="flex items-center space-x-3">
              <input
                type="month"
                value={selectedDprTargetsMonthValue}
                onChange={(e) => {
                  const selectedLabel = e.target.value;
                  const selected = dprMonths.find((month) => month.month_label === selectedLabel);
                  setSelectedDprTargetsMonthId(selected ? String(selected.id) : '');
                }}
                className="input max-w-xs"
              />
              <button
                onClick={addDigitalPrMonth}
                className="btn btn-secondary inline-flex items-center h-10 px-4 whitespace-nowrap"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Month
              </button>
              <button
                onClick={() => {
                  setDprImportFile(null);
                      setDprImportResults(null);
                      setShowDprImportResults(false);
                  setShowDprImportForm(true);
                }}
                className="btn btn-primary inline-flex items-center gap-2 h-10 px-4 whitespace-nowrap"
              >
                <Plus className="h-4 w-4 mr-2" />
                Import Digital PR CSV
              </button>
            </div>
          </div>

          <div className="card">
            <div className="space-y-4">
              {dprTargetsForSelectedMonth.length === 0 ? (
                <div className="text-center py-8">
                  <Briefcase className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No Digital PR targets for this month</p>
                </div>
              ) : (
                dprTargetsByClient.map((group) => (
                  <details key={group.key} className="border border-gray-200 rounded-lg">
                    <summary className="cursor-pointer select-none px-4 py-3 flex items-center justify-between bg-gray-50 rounded-t-lg">
                      <div className="flex items-center space-x-2">
                        <Briefcase className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-gray-900">{group.label}</span>
                      </div>
                      <span className="text-sm text-gray-500">{group.rows.length}</span>
                    </summary>
                    <div className="p-4 space-y-3">
                      {group.rows.map((row) => (
                        <div key={row.id} className="border border-gray-200 rounded-lg p-3">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-gray-900">{row.article_title || 'Untitled'}</p>
                              <p className="text-sm text-gray-500">
                                Writer: {row.writer_name_override || row.writer_full_name || row.writer_username || '—'}
                              </p>
                              <p className="text-sm text-gray-500">
                                Month: {row.month_start ? String(row.month_start).slice(0, 7) : '—'}
                              </p>
                            </div>
                            <span className="text-sm text-gray-600">
                              {row.currency_code || 'GBP'} {Number(row.price || 0).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </details>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      
{activeTab === 'reporting' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">Reporting</h2>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="btn btn-secondary inline-flex items-center h-10 px-4 whitespace-nowrap"
                onClick={() => exportCsv(
                  `reporting-detailed-${new Date().toISOString().slice(0, 10)}.csv`,
                  ['Service', 'Date', 'Client', 'User', 'Title', 'Status', 'Amount GBP'],
                  reportFilteredRows.map((row) => [
                    row.serviceLabel,
                    row.eventDate ? String(row.eventDate).slice(0, 10) : '',
                    row.clientName,
                    row.userName,
                    row.title,
                    row.status,
                    Number(row.amountGbp || 0).toFixed(2)
                  ])
                )}
              >
                Export Detailed CSV
              </button>
            </div>
          </div>

          <div className="card">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div>
                <label className="label">From</label>
                <input type="date" className="input" value={reportDateFrom} onChange={(e) => setReportDateFrom(e.target.value)} />
              </div>
              <div>
                <label className="label">To</label>
                <input type="date" className="input" value={reportDateTo} onChange={(e) => setReportDateTo(e.target.value)} />
              </div>
              <div>
                <label className="label">Client</label>
                <select className="input" value={reportClientId} onChange={(e) => setReportClientId(e.target.value)}>
                  <option value="">All Clients</option>
                  {reportClientOptions.map((client) => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">User</label>
                <select className="input" value={reportUserId} onChange={(e) => setReportUserId(e.target.value)}>
                  <option value="">All Users</option>
                  {reportUserOptions.map((user) => (
                    <option key={user.id} value={user.id}>{user.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Service</label>
                <select className="input" value={reportService} onChange={(e) => setReportService(e.target.value)}>
                  <option value="all">All Services</option>
                  <option value="content">Content</option>
                  <option value="link_building">Link Building</option>
                  <option value="digital_pr">Digital PR</option>
                </select>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
            <div className="card">
              <p className="text-xs uppercase text-gray-500">Total Items</p>
              <p className="text-2xl font-semibold text-gray-900">{reportingSummary.totalItems}</p>
            </div>
            <div className="card">
              <p className="text-xs uppercase text-gray-500">Content</p>
              <p className="text-2xl font-semibold text-gray-900">{reportingSummary.contentCount}</p>
            </div>
            <div className="card">
              <p className="text-xs uppercase text-gray-500">Link Building</p>
              <p className="text-2xl font-semibold text-gray-900">{reportingSummary.linkBuildingCount}</p>
            </div>
            <div className="card">
              <p className="text-xs uppercase text-gray-500">Digital PR</p>
              <p className="text-2xl font-semibold text-gray-900">{reportingSummary.digitalPrCount}</p>
            </div>
            <div className="card">
              <p className="text-xs uppercase text-gray-500">Clients</p>
              <p className="text-2xl font-semibold text-gray-900">{reportingSummary.clientCount}</p>
            </div>
            <div className="card">
              <p className="text-xs uppercase text-gray-500">Users</p>
              <p className="text-2xl font-semibold text-gray-900">{reportingSummary.userCount}</p>
            </div>
            <div className="card">
              <p className="text-xs uppercase text-gray-500">Total Cost (GBP)</p>
              <p className="text-2xl font-semibold text-gray-900">£{reportingSummary.totalCostGbp.toFixed(2)}</p>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium text-gray-900">Client Reports</h3>
              <button
                type="button"
                className="btn btn-secondary inline-flex items-center h-10 px-4 whitespace-nowrap"
                onClick={() => exportCsv(
                  `reporting-clients-${new Date().toISOString().slice(0, 10)}.csv`,
                  ['Client', 'Total Items', 'Content Items', 'Link Building Items', 'Digital PR Items', 'Cost GBP'],
                  reportByClient.map((row) => [
                    row.clientName,
                    row.totalItems,
                    row.contentCount,
                    row.linkBuildingCount,
                    row.digitalPrCount,
                    row.totalCostGbp.toFixed(2)
                  ])
                )}
              >
                Export Client CSV
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Content</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Link Building</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Digital PR</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cost GBP</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reportByClient.map((row) => (
                    <tr key={row.clientId}>
                      <td className="px-4 py-3 text-sm text-gray-900">{row.clientName}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right">{row.totalItems}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right">{row.contentCount}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right">{row.linkBuildingCount}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right">{row.digitalPrCount}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right">{row.totalCostGbp.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium text-gray-900">User Reports</h3>
              <button
                type="button"
                className="btn btn-secondary inline-flex items-center h-10 px-4 whitespace-nowrap"
                onClick={() => exportCsv(
                  `reporting-users-${new Date().toISOString().slice(0, 10)}.csv`,
                  ['User', 'Total Items', 'Content Items', 'Link Building Items', 'Digital PR Items', 'Cost GBP'],
                  reportByUser.map((row) => [
                    row.userName,
                    row.totalItems,
                    row.contentCount,
                    row.linkBuildingCount,
                    row.digitalPrCount,
                    row.totalCostGbp.toFixed(2)
                  ])
                )}
              >
                Export User CSV
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Content</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Link Building</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Digital PR</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cost GBP</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reportByUser.map((row) => (
                    <tr key={row.userId}>
                      <td className="px-4 py-3 text-sm text-gray-900">{row.userName}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right">{row.totalItems}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right">{row.contentCount}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right">{row.linkBuildingCount}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right">{row.digitalPrCount}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right">{row.totalCostGbp.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium text-gray-900">Financial Reports</h3>
              <button
                type="button"
                className="btn btn-secondary inline-flex items-center h-10 px-4 whitespace-nowrap"
                onClick={() => exportCsv(
                  `reporting-financial-${new Date().toISOString().slice(0, 10)}.csv`,
                  ['Service', 'Items', 'Total Cost GBP', 'Average Cost GBP'],
                  reportFinancialRows.map((row) => [
                    row.service,
                    row.items,
                    row.totalCostGbp.toFixed(2),
                    row.avgCostGbp.toFixed(2)
                  ])
                )}
              >
                Export Financial CSV
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Items</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Cost GBP</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Avg Cost GBP</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reportFinancialRows.map((row) => (
                    <tr key={row.service}>
                      <td className="px-4 py-3 text-sm text-gray-900">{row.service}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right">{row.items}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right">{row.totalCostGbp.toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right">{row.avgCostGbp.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-900">Website Frequency Report (Per Client)</h3>
              <p className="text-sm text-gray-500">How often links were placed on each website URL</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div>
                <label className="label" htmlFor="frequency-client">Client</label>
                <select
                  id="frequency-client"
                  className="input"
                  value={frequencyClientId}
                  onChange={(e) => {
                    setFrequencyClientId(e.target.value);
                    setFrequencyReportActive(false);
                  }}
                >
                  <option value="">Select client</option>
                  {frequencyClientOptions.map((client) => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label" htmlFor="frequency-date-from">Start Date</label>
                <input
                  id="frequency-date-from"
                  type="date"
                  className="input"
                  value={frequencyDateFrom}
                  onChange={(e) => {
                    setFrequencyDateFrom(e.target.value);
                    setFrequencyReportActive(false);
                  }}
                />
              </div>
              <div>
                <label className="label" htmlFor="frequency-date-to">End Date</label>
                <input
                  id="frequency-date-to"
                  type="date"
                  className="input"
                  value={frequencyDateTo}
                  onChange={(e) => {
                    setFrequencyDateTo(e.target.value);
                    setFrequencyReportActive(false);
                  }}
                />
              </div>
            </div>

            <button
              type="button"
              className="btn btn-primary inline-flex items-center h-10 px-4 whitespace-nowrap mb-4"
              onClick={runWebsiteFrequencyReport}
            >
              Run Website Frequency Report
            </button>

            {!frequencyReportActive ? (
              <p className="text-sm text-gray-500">
                Select a client and optional date range, then click Run Website Frequency Report.
              </p>
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <div className="text-sm text-gray-700">
                    <p>
                      <span className="font-medium">Client:</span> {frequencyClientName}
                    </p>
                    <p className="text-gray-500">
                      {(frequencyAppliedFilters.dateFrom || frequencyAppliedFilters.dateTo)
                        ? `Date range: ${frequencyAppliedFilters.dateFrom || '…'} → ${frequencyAppliedFilters.dateTo || '…'}`
                        : 'Date range: all dates'}
                      {' · '}
                      Websites found: {websiteFrequencyRows.length}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary inline-flex items-center h-10 px-4 whitespace-nowrap"
                    disabled={websiteFrequencyRows.length === 0}
                    onClick={() => exportCsv(
                      `reporting-website-frequency-${frequencyClientName.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.csv`,
                      ['Root Domain', 'Placements', 'Distinct URLs', 'First Placement', 'Last Placement'],
                      websiteFrequencyRows.map((row) => [
                        row.rootDomain,
                        row.placements,
                        row.distinctUrlsCount,
                        row.firstPlacementDate,
                        row.lastPlacementDate
                      ])
                    )}
                  >
                    Export Website Frequency CSV
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Root Domain</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Placements</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Distinct URLs</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">First Placement</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Last Placement</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {websiteFrequencyRows.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-4 py-6 text-sm text-center text-gray-500">
                            No website placements found for {frequencyClientName} with the selected date range.
                          </td>
                        </tr>
                      ) : websiteFrequencyRows.map((row) => {
                        const isExpanded = expandedFrequencyDomain === row.rootDomain;
                        return (
                          <React.Fragment key={row.rootDomain}>
                            <tr>
                              <td className="px-4 py-3 text-sm text-gray-900">{row.rootDomain}</td>
                              <td className="px-4 py-3 text-sm text-gray-700 text-right">{row.placements}</td>
                              <td className="px-4 py-3 text-sm text-gray-700 text-right">{row.distinctUrlsCount}</td>
                              <td className="px-4 py-3 text-sm text-gray-700 text-right">{row.firstPlacementDate || '—'}</td>
                              <td className="px-4 py-3 text-sm text-gray-700 text-right">{row.lastPlacementDate || '—'}</td>
                            </tr>
                            <tr>
                              <td colSpan={5} className="px-4 pt-0 pb-3">
                                <button
                                  type="button"
                                  className="text-sm text-primary-700 hover:text-primary-800 underline"
                                  onClick={() => setExpandedFrequencyDomain((prev) => (prev === row.rootDomain ? '' : row.rootDomain))}
                                >
                                  {isExpanded ? 'Hide distinct URLs' : `View distinct URLs (${row.distinctUrlsCount})`}
                                </button>
                                {isExpanded && (
                                  <ul className="mt-2 ml-5 list-disc text-sm text-gray-700 space-y-1">
                                    {row.distinctUrls.map((url) => (
                                      <li key={url} className="break-all">{url}</li>
                                    ))}
                                  </ul>
                                )}
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Import Targets Modal */}
      {showImportForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Import Targets (CSV)</h3>
              <p className="text-sm text-gray-600 mb-2">
                Required headers: title (or Article Title/Page URL) — plus user_full_name (recommended), user_email, or user_id — and either week_id or (week_number + year). Optional: keyword (pipe or comma separated for multiple), search volume (aligned pipe/comma list), notes, client_url, content_url, status (Pending / In Progress / Completed), QC&apos;d, Added To Client Sheet, and client_name
              </p>
              <p className="text-sm text-gray-600 mb-2">
                Re-importing the same user, week, and article title updates the existing row instead of creating a duplicate.
              </p>
              <p className="text-sm text-gray-600 mb-4">
                <a
                  href="/templates/targets-import-template.csv"
                  className="text-primary-600 hover:text-primary-700 underline"
                  download
                >
                  Download import template
                </a>
              </p>
              <form onSubmit={importTargets} className="space-y-4">
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  className="input"
                  required
                />
                <div className="flex space-x-3">
                  <button type="submit" className="btn btn-primary flex-1">Import</button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowImportForm(false);
                      setImportFile(null);
                    }}
                    className="btn btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showLbImportForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Import Link Building Targets (CSV)</h3>
              <p className="text-sm text-gray-600 mb-2">
                Required headers: Client Name, Month Name, Month Year, Article Title.
                Optional fields from the legacy importer are supported (Writer Full Name, External Writer, URLs, QC, DR/TF/CF, Anchor Text, Page Linked, Contact, Price, Currency, Live, Approved, Notes).
              </p>
              <p className="text-sm text-gray-600 mb-4">
                <a
                  href="/templates/link-building-import-template.csv"
                  className="text-primary-600 hover:text-primary-700 underline"
                  download
                >
                  Download link-building import template
                </a>
              </p>
              <form onSubmit={importLinkBuildingTargets} className="space-y-4">
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => setLbImportFile(e.target.files?.[0] || null)}
                  className="input"
                  required
                />
                <div className="flex space-x-3">
                  <button type="submit" className="btn btn-primary flex-1">Import</button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowLbImportForm(false);
                      setLbImportFile(null);
                    }}
                    className="btn btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showLbImportResults && lbImportResults && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Link Building Import Results</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <p className="text-xs uppercase text-gray-500">Inserted</p>
                <p className="text-lg font-semibold text-gray-900">{lbImportResults.inserted}</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <p className="text-xs uppercase text-gray-500">Updated Existing</p>
                <p className="text-lg font-semibold text-gray-900">{lbImportResults.updatedExisting}</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <p className="text-xs uppercase text-gray-500">Duplicates Removed</p>
                <p className="text-lg font-semibold text-gray-900">{lbImportResults.deduped ?? 0}</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <p className="text-xs uppercase text-gray-500">Skipped</p>
                <p className="text-lg font-semibold text-gray-900">{lbImportResults.skipped}</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <p className="text-xs uppercase text-gray-500">Failed</p>
                <p className="text-lg font-semibold text-gray-900">{lbImportResults.failed}</p>
              </div>
            </div>
            {Array.isArray(lbImportResults.errors) && lbImportResults.errors.length > 0 ? (
              <div className="border border-gray-200 rounded-lg max-h-72 overflow-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Row</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {lbImportResults.errors.map((item, idx) => (
                      <tr key={`${item.row}-${idx}`}>
                        <td className="px-3 py-2 text-sm text-gray-700">{item.row}</td>
                        <td className="px-3 py-2 text-sm text-gray-700">{item.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No row-level errors reported.</p>
            )}
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowLbImportResults(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      {showDprImportForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Import Digital PR Targets (CSV)</h3>
              <p className="text-sm text-gray-600 mb-2">
                Required headers: Client Name, Month Name, Month Year, Article Title.
                Optional fields from the legacy importer are supported (Writer Full Name, External Writer, URLs, QC, DR/TF/CF, Anchor Text, Page Linked, Contact, Price, Currency, Live, Approved, Notes).
              </p>
              <p className="text-sm text-gray-600 mb-4">
                <a
                  href="/templates/digital-pr-import-template.csv"
                  className="text-primary-600 hover:text-primary-700 underline"
                  download
                >
                  Download Digital PR import template
                </a>
              </p>
              <form onSubmit={importDigitalPrTargets} className="space-y-4">
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => setDprImportFile(e.target.files?.[0] || null)}
                  className="input"
                  required
                />
                <div className="flex space-x-3">
                  <button type="submit" className="btn btn-primary flex-1">Import</button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowDprImportForm(false);
                      setDprImportFile(null);
                    }}
                    className="btn btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showDprImportResults && dprImportResults && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Digital PR Import Results</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <p className="text-xs uppercase text-gray-500">Inserted</p>
                <p className="text-lg font-semibold text-gray-900">{dprImportResults.inserted}</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <p className="text-xs uppercase text-gray-500">Updated Existing</p>
                <p className="text-lg font-semibold text-gray-900">{dprImportResults.updatedExisting}</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <p className="text-xs uppercase text-gray-500">Duplicates Removed</p>
                <p className="text-lg font-semibold text-gray-900">{dprImportResults.deduped ?? 0}</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <p className="text-xs uppercase text-gray-500">Skipped</p>
                <p className="text-lg font-semibold text-gray-900">{dprImportResults.skipped}</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <p className="text-xs uppercase text-gray-500">Failed</p>
                <p className="text-lg font-semibold text-gray-900">{dprImportResults.failed}</p>
              </div>
            </div>
            {Array.isArray(dprImportResults.errors) && dprImportResults.errors.length > 0 ? (
              <div className="border border-gray-200 rounded-lg max-h-72 overflow-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Row</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {dprImportResults.errors.map((item, idx) => (
                      <tr key={`${item.row}-${idx}`}>
                        <td className="px-3 py-2 text-sm text-gray-700">{item.row}</td>
                        <td className="px-3 py-2 text-sm text-gray-700">{item.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No row-level errors reported.</p>
            )}
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowDprImportResults(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Form Modal */}
      {showUserForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingUser ? 'Edit User' : 'Add New User'}
              </h3>
              <form onSubmit={createUser} className="space-y-4">
                <div>
                  <label className="label">Username</label>
                  <input
                    type="text"
                    value={newUser.username}
                    onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="label">Full Name</label>
                  <input
                    type="text"
                    value={newUser.full_name}
                    onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                    className="input"
                  />
                </div>
                <div>
                  <label className="label">Email</label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="label">Password</label>
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="label">Role</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                    className="input"
                  >
                    <option value="user">User</option>
                    <option value="compliance">Compliance</option>
                    <option value="link_manager">Link Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="flex space-x-3">
                  <button type="submit" className="btn btn-primary flex-1">
                    {editingUser ? 'Save Changes' : 'Create User'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowUserForm(false);
                      setEditingUser(null);
                      setNewUser({ username: '', full_name: '', email: '', password: '', role: 'user' });
                    }}
                    className="btn btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                </div>
                {editingUser && (
                  <button
                    type="button"
                    onClick={deleteUser}
                    className="btn bg-red-600 text-white hover:bg-red-700 w-full flex items-center justify-center"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete User
                  </button>
                )}
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Week Form Modal */}
      {showWeekForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingWeek ? 'Edit Week' : 'Add New Week'}
              </h3>
              <form onSubmit={createWeek} className="space-y-4">
                <div>
                  <label className="label">Week Number</label>
                  <input
                    type="number"
                    value={newWeek.week_number}
                    onChange={(e) => setNewWeek({...newWeek, week_number: e.target.value})}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="label">Year</label>
                  <input
                    type="number"
                    value={newWeek.year}
                    onChange={(e) => setNewWeek({...newWeek, year: e.target.value})}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="label">Start Date</label>
                  <input
                    type="date"
                    value={newWeek.start_date}
                    onChange={(e) => setNewWeek({...newWeek, start_date: e.target.value})}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="label">End Date</label>
                  <input
                    type="date"
                    value={newWeek.end_date}
                    onChange={(e) => setNewWeek({...newWeek, end_date: e.target.value})}
                    className="input"
                    required
                  />
                </div>
                <div className="flex space-x-3">
                  <button type="submit" className="btn btn-primary flex-1">
                    {editingWeek ? 'Save Changes' : 'Create Week'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowWeekForm(false);
                      setEditingWeek(null);
                      setNewWeek({ week_number: '', year: '', start_date: '', end_date: '' });
                    }}
                    className="btn btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                </div>
                {editingWeek && (
                  <button
                    type="button"
                    onClick={deleteWeek}
                    className="btn bg-red-600 text-white hover:bg-red-700 w-full flex items-center justify-center"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Week
                  </button>
                )}
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Client Form Modal */}
      {showClientForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingClient ? 'Edit Client' : 'Add New Client'}
              </h3>
              <form onSubmit={createClient} className="space-y-4">
                <div>
                  <label className="label">Client Name *</label>
                  <input
                    type="text"
                    value={newClient.name}
                    onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="label">Website URL</label>
                  <input
                    type="url"
                    value={newClient.website_url}
                    onChange={(e) => setNewClient({ ...newClient, website_url: e.target.value })}
                    className="input"
                    placeholder="https://example.com/"
                  />
                </div>
                <div>
                  <label className="label">Status</label>
                  <select
                    value={newClient.status}
                    onChange={(e) => setNewClient({ ...newClient, status: e.target.value })}
                    className="input"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  <p className="mt-1 text-sm text-gray-500">
                    Inactive clients stop appearing on dashboard cards from the selected month onward.
                  </p>
                </div>
                <div className="border-t border-gray-200 pt-4 space-y-3">
                  <h4 className="text-sm font-semibold text-gray-900">Services</h4>
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={Boolean(newClient.uses_content)}
                      onChange={(e) => setNewClient((prev) => ({ ...prev, uses_content: e.target.checked }))}
                    />
                    <span className="text-sm text-gray-700">Content</span>
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={Boolean(newClient.uses_link_building)}
                      onChange={(e) => setNewClient((prev) => ({ ...prev, uses_link_building: e.target.checked }))}
                    />
                    <span className="text-sm text-gray-700">Link Building</span>
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={Boolean(newClient.uses_digital_pr)}
                      onChange={(e) => setNewClient((prev) => ({ ...prev, uses_digital_pr: e.target.checked }))}
                    />
                    <span className="text-sm text-gray-700">Digital PR</span>
                  </label>
                  <p className="text-sm text-gray-500">
                    Select at least one service for this client.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Boolean(newClient.uses_content) && (
                    <div className="border-t border-gray-200 pt-4 space-y-2">
                      <h4 className="text-sm font-semibold text-gray-900">Content Monthly Target</h4>
                      <input
                        type="number"
                        step="0.01"
                        className="input"
                        value={newClient.content_monthly_target ?? ''}
                        onChange={(e) => setNewClient((prev) => ({ ...prev, content_monthly_target: e.target.value }))}
                        placeholder="e.g. 30"
                      />
                      <p className="text-sm text-gray-500">
                        Used for content reporting targets in the merged system.
                      </p>
                    </div>
                  )}
                  {Boolean(newClient.uses_link_building) && (
                    <div className="border-t border-gray-200 pt-4 space-y-3">
                      <h4 className="text-sm font-semibold text-gray-900">Link Building Monthly Target</h4>
                      <div className="grid grid-cols-1 gap-3">
                        <div>
                          <label className="label">Target Month</label>
                          <select
                            className="input"
                            value={selectedLbTargetMonthId}
                            onChange={(e) => setSelectedLbTargetMonthId(e.target.value)}
                            disabled={lbMonths.length === 0}
                          >
                            {lbMonths.length === 0 ? (
                              <option value="">No link-building months found</option>
                            ) : (
                              lbMonths.map((month) => (
                                <option key={month.id} value={month.id}>
                                  {month.month_label}
                                </option>
                              ))
                            )}
                          </select>
                        </div>
                        <div>
                          <label className="label">Monthly Target</label>
                          <input
                            type="number"
                            step="0.01"
                            className="input"
                            value={
                              editingClient
                                ? (monthlyTargetDrafts[String(selectedLbTargetMonthId)] ?? '')
                                : (newClient.lb_monthly_target ?? '')
                            }
                            onChange={(e) => {
                              const nextValue = e.target.value;
                              if (editingClient) {
                                setMonthlyTargetDrafts((prev) => ({
                                  ...prev,
                                  [String(selectedLbTargetMonthId)]: nextValue
                                }));
                              } else {
                                setNewClient((prev) => ({ ...prev, lb_monthly_target: nextValue }));
                              }
                            }}
                            placeholder="e.g. 30"
                          />
                        </div>
                        {editingClient && (
                          <div>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => saveClientMonthlyTarget(selectedLbTargetMonthId)}
                              disabled={
                                !selectedLbTargetMonthId
                                || Boolean(savingMonthlyTargetByMonthId[String(selectedLbTargetMonthId)])
                              }
                            >
                              {savingMonthlyTargetByMonthId[String(selectedLbTargetMonthId)] ? 'Saving...' : 'Save Monthly Target'}
                            </button>
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">
                        Target carries to future months until a new monthly target is set.
                      </p>
                    </div>
                  )}
                  {Boolean(newClient.uses_digital_pr) && (
                    <div className="border-t border-gray-200 pt-4 space-y-3">
                      <h4 className="text-sm font-semibold text-gray-900">Digital PR Monthly Target</h4>
                      <div className="grid grid-cols-1 gap-3">
                        <div>
                          <label className="label">Target Month</label>
                          <select
                            className="input"
                            value={selectedDprTargetMonthId}
                            onChange={(e) => setSelectedDprTargetMonthId(e.target.value)}
                            disabled={dprMonths.length === 0}
                          >
                            {dprMonths.length === 0 ? (
                              <option value="">No Digital PR months found</option>
                            ) : (
                              dprMonths.map((month) => (
                                <option key={month.id} value={month.id}>
                                  {month.month_label}
                                </option>
                              ))
                            )}
                          </select>
                        </div>
                        <div>
                          <label className="label">Monthly Target</label>
                          <input
                            type="number"
                            step="0.01"
                            className="input"
                            value={
                              editingClient
                                ? (dprMonthlyTargetDrafts[String(selectedDprTargetMonthId)] ?? '')
                                : (newClient.dpr_monthly_target ?? '')
                            }
                            onChange={(e) => {
                              const nextValue = e.target.value;
                              if (editingClient) {
                                setDprMonthlyTargetDrafts((prev) => ({
                                  ...prev,
                                  [String(selectedDprTargetMonthId)]: nextValue
                                }));
                              } else {
                                setNewClient((prev) => ({ ...prev, dpr_monthly_target: nextValue }));
                              }
                            }}
                            placeholder="e.g. 30"
                          />
                        </div>
                        {editingClient && (
                          <div>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => saveClientDprMonthlyTarget(selectedDprTargetMonthId)}
                              disabled={
                                !selectedDprTargetMonthId
                                || Boolean(savingDprMonthlyTargetByMonthId[String(selectedDprTargetMonthId)])
                              }
                            >
                              {savingDprMonthlyTargetByMonthId[String(selectedDprTargetMonthId)] ? 'Saving...' : 'Save Monthly Target'}
                            </button>
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">
                        Target carries to future months until a new monthly target is set.
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex space-x-3">
                  <button type="submit" className="btn btn-primary flex-1">
                    {editingClient ? 'Save Changes' : 'Create Client'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowClientForm(false);
                      setEditingClient(null);
                      setNewClient({
                        name: '',
                        website_url: '',
                        status: 'active',
                        lb_monthly_target: '',
                        dpr_monthly_target: '',
                        uses_content: true,
                        uses_link_building: true,
                        uses_digital_pr: false,
                        content_monthly_target: ''
                      });
                      setMonthlyTargetDrafts({});
                      setDprMonthlyTargetDrafts({});
                      setSelectedLbTargetMonthId(getDefaultLbMonthId(lbMonths));
                      setSelectedDprTargetMonthId(getDefaultLbMonthId(dprMonths));
                    }}
                    className="btn btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                </div>
                {editingClient && (
                  <button
                    type="button"
                    onClick={deleteClient}
                    className="btn bg-red-600 text-white hover:bg-red-700 w-full flex items-center justify-center"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Client
                  </button>
                )}
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Target Form Modal */}
      {showTargetForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-full max-w-3xl shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {editingTarget ? 'Edit Target' : 'Add New Target'}
            </h3>
            <form onSubmit={createTarget} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">User</label>
                  <select
                    value={newTarget.user_id}
                    onChange={(e) => setNewTarget({ ...newTarget, user_id: e.target.value })}
                    className="input"
                    required
                  >
                    <option value="">Select User</option>
                    {users.filter((u) => u.role === 'user').map((user) => (
                      <option key={user.id} value={user.id}>{user.username}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">Week (any day in the week)</label>
                  <input
                    type="date"
                    value={targetFormWeekDateInputValue}
                    onChange={(e) => {
                      const picked = e.target.value;
                      const week = findWeekContainingDate(weeks, picked);
                      if (!week) {
                        toast.error('No week covers that date — pick a day inside an existing week range');
                        setNewTarget({ ...newTarget, week_id: '' });
                        return;
                      }
                      setNewTarget({ ...newTarget, week_id: String(week.id) });
                    }}
                    className="input"
                    required
                  />
                  {newTarget.week_id && targetFormWeekSummary && (
                    <p className="mt-1 text-xs text-gray-500">{targetFormWeekSummary}</p>
                  )}
                </div>
                <div>
                  <label className="label">Client</label>
                  <select
                    value={newTarget.client_id}
                    onChange={(e) => setNewTarget({ ...newTarget, client_id: e.target.value })}
                    className="input"
                  >
                    <option value="">No client</option>
                    {clients.filter((client) => client.uses_content !== 0 && client.uses_content !== false).map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}{client.status !== 'active' ? ' (inactive)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                {editingTarget ? (
                  <div>
                    <label className="label">Status</label>
                    <select
                      value={newTarget.status || 'pending'}
                      onChange={(e) => setNewTarget({ ...newTarget, status: e.target.value })}
                      className="input"
                    >
                      <option value="pending">Pending</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                ) : (
                  <div className="hidden md:block" aria-hidden="true" />
                )}
                <div className="md:col-span-2">
                  <label className="label">Title</label>
                  <input
                    type="text"
                    value={newTarget.title}
                    onChange={(e) => setNewTarget({ ...newTarget, title: e.target.value })}
                    className="input"
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="label">Client URL</label>
                  <input
                    type="text"
                    value={newTarget.client_url || ''}
                    onChange={(e) => setNewTarget({ ...newTarget, client_url: e.target.value })}
                    className="input"
                    placeholder="https://..."
                  />
                </div>
                <div className="md:col-span-2">
                  <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_auto] gap-3 items-end mb-2">
                    <div>
                      <label className="label mb-0">Keyword(s)</label>
                    </div>
                    <div>
                      <label className="label mb-0">
                        Search Volume
                        <span className="block font-normal normal-case text-gray-500 text-xs tracking-normal">
                          (UK search volume)
                        </span>
                      </label>
                    </div>
                    <div className="w-10" aria-hidden="true" />
                  </div>
                  <div className="space-y-2">
                    {(newTarget.keywordRows || [emptyKeywordRow()]).map((row, index) => (
                      <div
                        key={`keyword-row-${index}`}
                        className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_auto] gap-3 items-center"
                      >
                        <input
                          type="text"
                          value={row.keyword || ''}
                          onChange={(e) => updateKeywordRow(index, 'keyword', e.target.value)}
                          className="input"
                          placeholder={index === 0 ? 'e.g. World Cup Odds' : 'Add another keyword'}
                        />
                        <div className="relative">
                          <input
                            type="text"
                            value={row.loading ? 'Fetching…' : formatSearchVolume(row.search_volume)}
                            className="input bg-gray-50 pr-10"
                            readOnly
                            placeholder="—"
                          />
                          {row.loading && (
                            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-primary-600" />
                          )}
                        </div>
                        <button
                          type="button"
                          className="btn btn-secondary py-2 px-2"
                          onClick={() => removeKeywordRow(index)}
                          title="Remove keyword"
                          disabled={(newTarget.keywordRows || []).length <= 1 && !row.keyword}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary mt-2 inline-flex items-center gap-1"
                    onClick={addKeywordRow}
                  >
                    <Plus className="h-4 w-4" />
                    Add keyword
                  </button>
                </div>
                <div className="md:col-span-2">
                  <label className="label">Notes</label>
                  <textarea
                    value={newTarget.description}
                    onChange={(e) => setNewTarget({ ...newTarget, description: e.target.value })}
                    className="input"
                    rows="2"
                    placeholder="Optional notes"
                  />
                </div>
                {editingTarget && (
                  <div className="md:col-span-2">
                    <label className="label">Evidence</label>
                    <input
                      type="text"
                      value={newTarget.evidence || ''}
                      onChange={(e) => setNewTarget({ ...newTarget, evidence: e.target.value })}
                      className="input"
                      placeholder="Content URL or Google Doc link"
                    />
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-3 pt-2">
                <button type="submit" className="btn btn-primary">
                  {editingTarget ? 'Save Changes' : 'Create Target'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowTargetForm(false);
                    setEditingTarget(null);
                    setNewTarget(emptyTargetForm());
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                {editingTarget && (
                  <button
                    type="button"
                    onClick={deleteTarget}
                    className="btn bg-red-600 text-white hover:bg-red-700 inline-flex items-center ml-auto"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Target
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard; 